"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { VISIBLE_REWARD_CATEGORIES } from "../config/rewards-visibility";
import { rewardsService } from "../services/rewards.service";
import type { BadgeDetailResponse, BadgeCategoryResponse, BadgesResponse, CertificatesListResponse, CertificateItemResponse, LeaderboardPrivacyResponse, LeaderboardPrivacyUpdateRequest, LeaderboardResponse, ReferralAttributeResponse, ReferralHandleResponse, ReferralValidateResponse, ReferralsDashboardResponse, RewardsBadgeAreaCard, RewardBadgeAreaType, RewardBadgeCategoryType, RewardsBadgeCategoryCard, RewardsLeaderboardParams, RewardsSummaryResponse, SigmaScoreResponse, UseRewardsQueryOptions } from "../types";
const CATEGORY_TYPES: RewardBadgeCategoryType[] = ["general", "forex", "crypto", "futures"];
const AREA_TYPES: RewardBadgeAreaType[] = ["performance", "consistency", "automation", "community"];
const SUMMARY_STALE_TIME = 5 * 60 * 1000;
const BADGES_STALE_TIME = 10 * 60 * 1000;
const REFERRALS_STALE_TIME = 5 * 60 * 1000;
const CERTIFICATES_STALE_TIME = 30 * 1000;
const SIGMA_STALE_TIME = 5 * 60 * 1000;
const LEADERBOARD_STALE_TIME = 5 * 60 * 1000;
const LEADERBOARD_PRIVACY_STALE_TIME = 10 * 60 * 1000;
const VISIBLE_CATEGORY_SET = new Set<RewardBadgeCategoryType>(VISIBLE_REWARD_CATEGORIES);
function normalizeCategoryType(type?: string): RewardBadgeCategoryType | null {
  const normalized = (type ?? "").trim().toLowerCase();
  return CATEGORY_TYPES.includes(normalized as RewardBadgeCategoryType) ? normalized as RewardBadgeCategoryType : null;
}
function normalizeAreaType(type?: string): RewardBadgeAreaType | null {
  const normalized = (type ?? "").trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  return AREA_TYPES.includes(normalized as RewardBadgeAreaType) ? normalized as RewardBadgeAreaType : null;
}
function createDefaultBadgeArea(areaType: RewardBadgeAreaType): RewardsBadgeAreaCard {
  const label = areaType.charAt(0).toUpperCase() + areaType.slice(1);
  return {
    name: `${label} Badges`,
    description: `${label} achievements`,
    badges: []
  };
}
function toProgressPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}
function toLevel(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value > 0 ? Math.floor(value) : 0;
}
export function mapBadgeCategoriesToCards(categories: BadgeCategoryResponse[]): RewardsBadgeCategoryCard[] {
  const visibleCategories = categories.map(category => ({
    category,
    normalizedType: normalizeCategoryType(category.type)
  })).filter((value): value is {
    category: BadgeCategoryResponse;
    normalizedType: RewardBadgeCategoryType;
  } => value.normalizedType !== null && VISIBLE_CATEGORY_SET.has(value.normalizedType));
  return visibleCategories.map(({
    category,
    normalizedType
  }) => {
    const mappedAreas: Record<RewardBadgeAreaType, RewardsBadgeAreaCard> = {
      performance: createDefaultBadgeArea("performance"),
      consistency: createDefaultBadgeArea("consistency"),
      automation: createDefaultBadgeArea("automation"),
      community: createDefaultBadgeArea("community")
    };
    for (const area of category.areas) {
      const areaType = normalizeAreaType(area.type);
      if (!areaType) {
        continue;
      }
      mappedAreas[areaType] = {
        name: area.name,
        description: `${area.name} achievements`,
        badges: area.badges.map(badge => ({
          id: badge.id,
          slug: badge.slug,
          name: badge.name,
          description: badge.description,
          isUnlocked: Boolean(badge.is_unlocked),
          level: toLevel(badge.level),
          maxLevel: toLevel(badge.max_level) || 1,
          progress: toProgressPercent(badge.progress),
          nextThreshold: typeof badge.next_threshold === "number" ? badge.next_threshold : null,
          earnedAt: badge.earned_at ?? null,
          icon: badge.icon,
          iconUrl: badge.icon_url
        }))
      };
    }
    return {
      type: normalizedType,
      name: category.name,
      description: `${category.name} achievements`,
      areas: mappedAreas
    };
  });
}
export const REWARDS_QUERY_KEYS = {
  all: ["rewards"] as const,
  summary: () => [...REWARDS_QUERY_KEYS.all, "summary"] as const,
  referrals: () => [...REWARDS_QUERY_KEYS.all, "referrals"] as const,
  badges: () => [...REWARDS_QUERY_KEYS.all, "badges"] as const,
  badgeDetail: (badgeId: number) => [...REWARDS_QUERY_KEYS.all, "badge-detail", badgeId] as const,
  certificates: () => [...REWARDS_QUERY_KEYS.all, "certificates"] as const,
  sigmaScore: () => [...REWARDS_QUERY_KEYS.all, "sigma-score"] as const,
  leaderboardPrivacy: () => [...REWARDS_QUERY_KEYS.all, "leaderboard-privacy"] as const,
  leaderboard: (params?: RewardsLeaderboardParams) => [...REWARDS_QUERY_KEYS.all, "leaderboard", params ?? {}] as const
} as const;
export function useRewardsSummary(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.summary();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? SUMMARY_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp() {
  return rewardsService.getSummary();
}
export function useRewardsBadges(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.badges();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? BADGES_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp2,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp2() {
  return rewardsService.getBadges();
}
export function useRewardsReferrals(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.referrals();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? REFERRALS_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp3,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp3() {
  return rewardsService.getReferrals();
}
export function useUpdateReferralHandle() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: response => {
        queryClient.setQueryData(REWARDS_QUERY_KEYS.referrals(), current => current ? {
          ...current,
          referral_code: response.referral_code,
          referral_link: response.referral_link
        } : current);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp4(referralCode) {
  return rewardsService.updateReferralHandle(referralCode);
}
export function useValidateReferral() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp5(referralCode) {
  return rewardsService.validateReferral(referralCode);
}
export function useAttributeReferral() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: REWARDS_QUERY_KEYS.referrals()
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp6(referralCode) {
  return rewardsService.attributeReferral(referralCode);
}
export function useRewardsBadgeCards(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.badges();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? BADGES_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp7,
      select: _temp8,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp8(data) {
  return mapBadgeCategoriesToCards(data.categories);
}
function _temp7() {
  return rewardsService.getBadges();
}
export function useRewardsBadgeDetail(badgeId, options) {
  const $ = _c(11);
  let t0;
  if ($[0] !== badgeId) {
    t0 = Number.isFinite(badgeId) ? Number(badgeId) : 0;
    $[0] = badgeId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const resolvedBadgeId = t0;
  let t1;
  if ($[2] !== resolvedBadgeId) {
    t1 = REWARDS_QUERY_KEYS.badgeDetail(resolvedBadgeId);
    $[2] = resolvedBadgeId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== resolvedBadgeId) {
    t2 = () => rewardsService.getBadgeDetail(resolvedBadgeId);
    $[4] = resolvedBadgeId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = options?.staleTime ?? BADGES_STALE_TIME;
  const t4 = (options?.enabled ?? true) && resolvedBadgeId > 0;
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      staleTime: t3,
      enabled: t4
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t5);
}
export function useRewardsCertificates(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.certificates();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? CERTIFICATES_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp9,
      select: _temp10,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp10(data) {
  return data.certificates ?? [];
}
function _temp9() {
  return rewardsService.getCertificates();
}
export function useRewardsSigmaScore(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.sigmaScore();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? SIGMA_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp11,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp11() {
  return rewardsService.getSigmaScore();
}
export function useRewardsLeaderboard(params, options) {
  const $ = _c(9);
  let t0;
  if ($[0] !== params) {
    t0 = REWARDS_QUERY_KEYS.leaderboard(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = () => rewardsService.getLeaderboard(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = options?.staleTime ?? LEADERBOARD_STALE_TIME;
  const t3 = options?.enabled ?? true;
  let t4;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: t2,
      enabled: t3,
      placeholderData: _temp12
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
}
function _temp12(previousData) {
  return previousData;
}
export function useRewardsLeaderboardPrivacy(options) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = REWARDS_QUERY_KEYS.leaderboardPrivacy();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.staleTime ?? LEADERBOARD_PRIVACY_STALE_TIME;
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: _temp13,
      staleTime: t1,
      enabled: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return useQuery(t3);
}
function _temp13() {
  return rewardsService.getLeaderboardPrivacy();
}
export function useUpdateRewardsLeaderboardPrivacy() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp14,
      onSuccess: response => {
        queryClient.setQueryData(REWARDS_QUERY_KEYS.leaderboardPrivacy(), response);
        queryClient.invalidateQueries({
          queryKey: [...REWARDS_QUERY_KEYS.all, "leaderboard"]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp14(payload) {
  return rewardsService.updateLeaderboardPrivacy(payload);
}